import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BACKEND_URL } from '../../../Constants';

const CourseApplicantsPage = () => {
    const { id: courseId } = useParams();
    const [applicants, setApplicants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false);
    const [availableStudents, setAvailableStudents] = useState([]);
    const [selectedStudentId, setSelectedStudentId] = useState('');

    useEffect(() => {
        if (isAddStudentModalOpen) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [isAddStudentModalOpen]);

    const fetchApplicants = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${ BACKEND_URL }/api/enrollments/applicants/${courseId}`);
            console.log("Fetched Applicants Data:", response.data);
            setApplicants(response.data);
            return response.data;
        } catch (err) {
            console.error('Error fetching applicants:', err);
            setError('Failed to load applicants.');
            toast.error('Failed to load applicants.');
            return [];
        } finally {
            setLoading(false);
        }
    };

    const fetchAndFilterAvailableStudents = async (currentApplicants) => {
        try {
            // Ensure this endpoint exists and returns students with _id and name/email
            const allStudentsResponse = await axios.get(`${ BACKEND_URL }/api/users/students`);
            const allStudents = allStudentsResponse.data;

            const enrolledStudentIds = new Set(currentApplicants.map(applicant => applicant.studentId));

            const filteredStudents = allStudents.filter(student => !enrolledStudentIds.has(student._id));
            setAvailableStudents(filteredStudents);
            // Pre-select the first available student if any
            if (filteredStudents.length > 0) {
                setSelectedStudentId(filteredStudents[0]._id);
            } else {
                setSelectedStudentId(''); // Clear selection if no students
            }
        } catch (err) {
            console.error('Error fetching available students:', err);
            toast.error('Failed to load available students for manual enrollment.');
        }
    };

    useEffect(() => {
        const loadData = async () => {
            const currentApplicants = await fetchApplicants();
            await fetchAndFilterAvailableStudents(currentApplicants);
        };
        loadData();
    }, [courseId]);

    const handleApproval = async (studentId) => {
        try {
            await axios.put(`${ BACKEND_URL }/api/enrollments/approval`, {
                courseId: courseId,
                studentId: studentId,
                status: 'approved',
            });
            setApplicants(prevApplicants =>
                prevApplicants.map(applicant =>
                    applicant.studentId === studentId ? { ...applicant, status: 'approved' } : applicant
                )
            );
            toast.success('Applicant Approved.');
            // Re-fetch available students after approval, as one less student is 'available' now
            const updatedApplicants = await fetchApplicants();
            await fetchAndFilterAvailableStudents(updatedApplicants);
        } catch (err) {
            console.error('Error approving applicant:', err);
            setError('Failed to approve applicant.');
            toast.error('Failed to approve applicant.');
        }
    };

    const handleRejection = async (studentId) => {
        try {
            await axios.put(`${ BACKEND_URL }/api/enrollments/approval`, {
                courseId: courseId,
                studentId: studentId,
                status: 'rejected',
            });
            setApplicants(prevApplicants =>
                prevApplicants.map(applicant =>
                    applicant.studentId === studentId ? { ...applicant, status: 'rejected' } : applicant
                )
            );
            toast.info('Applicant Rejected.');
            const updatedApplicants = await fetchApplicants();
            await fetchAndFilterAvailableStudents(updatedApplicants);
        } catch (err) {
            console.error('Error rejecting applicant:', err);
            setError('Failed to reject applicant.');
            toast.error('Failed to reject applicant.');
        }
    };

    const handleCancelEnrollment = async (studentId) => {
        try {
            await axios.put(`${ BACKEND_URL }/api/enrollments/cancel`, {
                courseId: courseId,
                studentId: studentId,
            });
            setApplicants(prevApplicants =>
                prevApplicants.map(applicant =>
                    applicant.studentId === studentId ? { ...applicant, status: 'cancelled' } : applicant
                )
            );
            toast.warn('Enrollment Cancelled.');
            const updatedApplicants = await fetchApplicants();
            await fetchAndFilterAvailableStudents(updatedApplicants);
        } catch (err) {
            console.error('Error cancelling enrollment:', err);
            setError('Failed to cancel enrollment.');
            toast.error('Failed to cancel enrollment.');
        }
    };

    const handleRemoveEnrollment = async (enrollmentId, studentName) => {
        console.log("Attempting to remove enrollment with ID:", enrollmentId);

        if (!enrollmentId) {
            toast.error(`Cannot remove ${studentName}: Enrollment ID is missing.`);
            console.error("Error: enrollmentId is undefined for student", studentName);
            return;
        }

        if (!window.confirm(`Are you sure you want to permanently remove ${studentName}'s enrollment? This action cannot be undone.`)) {
            return;
        }
        try {
            // Optimistically update the UI first
            setApplicants(prevApplicants => prevApplicants.filter(app => app.enrollmentId !== enrollmentId));
            toast.info(`Attempting to remove ${studentName}'s enrollment...`);

            await axios.delete(`${ BACKEND_URL }/api/enrollments/${enrollmentId}`);
            toast.success(`${studentName}'s enrollment removed successfully.`);
            const updatedApplicants = await fetchApplicants();
            await fetchAndFilterAvailableStudents(updatedApplicants);
        } catch (err) {
            console.error('Error removing enrollment:', err);
            toast.error(`Failed to remove ${studentName}'s enrollment: ${err.response?.data?.message || err.message}`);
            fetchApplicants(); // Re-fetch to revert optimistic update if deletion fails
        }
    };

    const openAddStudentModal = () => {
        setIsAddStudentModalOpen(true);
        // Reset selected student ID when opening the modal
        setSelectedStudentId(availableStudents.length > 0 ? availableStudents[0]._id : '');
    };

    const closeAddStudentModal = () => {
        setIsAddStudentModalOpen(false);
    };

    const handleAddStudentManually = async (e) => {
        e.preventDefault();

        if (!selectedStudentId) {
            toast.error('Please select a student.');
            return;
        }

        try {
            const studentToEnroll = availableStudents.find(s => s._id === selectedStudentId);
            const studentName = studentToEnroll ? (studentToEnroll.name || studentToEnroll.email) : 'Unknown Student';

            const response = await axios.post(`${ BACKEND_URL }/api/enrollments/manual-enroll`, {
                courseId: courseId,
                studentId: selectedStudentId,
            });

            closeAddStudentModal();

            if (response.data.message.includes('already approved')) {
                toast.info(`Student ${studentName} is already enrolled and approved for this course.`);
            } else {
                toast.success(`Student ${studentName} manually enrolled and approved for the course.`);
            }
            
            // Re-fetch both applicants and available students to update all lists
            const updatedApplicants = await fetchApplicants();
            await fetchAndFilterAvailableStudents(updatedApplicants);

        } catch (err) {
            console.error('Error adding student manually:', err);
            toast.error(`Failed to add student manually: ${err.response?.data?.message || err.message}`);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <p className="text-muted fs-5">Loading applicants...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="alert alert-danger text-center" role="alert">
                    <strong className="font-weight-bold">Error!</strong>
                    <span className="d-block d-sm-inline"> {error}</span>
                </div>
            </div>
        );
    }

    // Always render the container and the table structure
    return (
        <div className="container py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="text-dark mb-0">Course Applicants</h2>
                <button
                    className="btn btn-primary shadow-sm"
                    onClick={openAddStudentModal}
                >
                    Add Student Manually
                </button>
            </div>

            <div className="card shadow-sm">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th scope="col" className="text-start text-uppercase text-muted small">#</th>
                                    <th scope="col" className="text-start text-uppercase text-muted small">Student ID</th>
                                    <th scope="col" className="text-start text-uppercase text-muted small">Student Name</th>
                                    <th scope="col" className="text-start text-uppercase text-muted small">Email</th>
                                    <th scope="col" className="text-start text-uppercase text-muted small">Status</th>
                                    <th scope="col" className="text-start text-uppercase text-muted small">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {applicants.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center text-muted py-3">
                                            No applicants found for this course.
                                        </td>
                                    </tr>
                                ) : (
                                    applicants.map((applicant, index) => (
                                        <tr key={applicant.studentId}>
                                            <td className="align-middle">{index + 1}</td>
                                            <td className="align-middle">{applicant.studentId}</td>
                                            <td className="align-middle">{applicant.studentName}</td>
                                            <td className="align-middle">{applicant.email}</td>
                                            <td className="align-middle text-capitalize">{applicant.status}</td>
                                            <td className="align-middle">
                                                {applicant.status === 'pending' && (
                                                    <>
                                                        <button
                                                            className="btn btn-success btn-sm me-2"
                                                            onClick={() => handleApproval(applicant.studentId)}
                                                        >
                                                            Approve
                                                        </button>
                                                        <button
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() => handleRejection(applicant.studentId)}
                                                        >
                                                            Reject
                                                        </button>
                                                    </>
                                                )}
                                                {applicant.status === 'approved' && (
                                                    <button
                                                        className="btn btn-warning btn-sm"
                                                        onClick={() => handleCancelEnrollment(applicant.studentId)}
                                                    >
                                                        Pause
                                                    </button>
                                                )}
                                                {applicant.status === 'rejected' && (
                                                    <>
                                                        <button
                                                            className="btn btn-success btn-sm me-2"
                                                            onClick={() => handleApproval(applicant.studentId)}
                                                        >
                                                            Approve
                                                        </button>
                                                        <span className="text-danger ml-2">Rejected</span>
                                                    </>
                                                )}
                                                {applicant.status === 'cancelled' && (
                                                    <>
                                                        <button
                                                            className="btn btn-primary btn-sm me-2"
                                                            onClick={() => handleApproval(applicant.studentId)}
                                                        >
                                                            Resume Course
                                                        </button>
                                                        <span className="text-danger ml-2">Cancelled</span>
                                                    </>
                                                )}
                                                {/* Remove button, available for all statuses */}
                                                <button
                                                    className="btn btn-danger btn-sm ms-2"
                                                    onClick={() => {
                                                        console.log("Applicant object before remove:", applicant);
                                                        handleRemoveEnrollment(applicant.enrollmentId, applicant.studentName);
                                                    }}
                                                >
                                                    Remove
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <Link to={`/dashboard/courses/${courseId}`} className="btn btn-secondary mt-3">Back to Course</Link>

            {/* Add Student Manually Modal */}
            {isAddStudentModalOpen && (
                <div
                    className="modal d-block"
                    tabIndex="-1"
                    role="dialog"
                    aria-modal="true"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
                >
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content rounded-lg shadow-lg">
                            <div className="modal-header">
                                <h5 className="modal-title">Add Existing Student</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Close"
                                    onClick={closeAddStudentModal}
                                ></button>
                            </div>
                            <form onSubmit={handleAddStudentManually}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label htmlFor="selectStudent" className="form-label">Select Student:</label>
                                        <select
                                            className="form-select rounded"
                                            id="selectStudent"
                                            value={selectedStudentId}
                                            onChange={(e) => setSelectedStudentId(e.target.value)}
                                            required
                                            // Disable if no available students
                                            disabled={availableStudents.length === 0}
                                        >
                                            <option value="">
                                                {availableStudents.length === 0 ? "-- No students available --" : "-- Select a student --"}
                                            </option>
                                            {availableStudents.map(student => (
                                                <option key={student._id} value={student._id}>
                                                    {student.name} ({student.email})
                                                </option>
                                            ))}
                                        </select>
                                        {availableStudents.length === 0 && (
                                            <small className="text-muted mt-2 d-block">All students are already enrolled in this course or no students exist.</small>
                                        )}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary rounded"
                                        onClick={closeAddStudentModal}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary rounded"
                                        // Disable submit if no student is selected or no students are available
                                        disabled={!selectedStudentId || availableStudents.length === 0}
                                    >
                                        Enroll Student
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
            <ToastContainer position="top-right" autoClose={2000} />
        </div>
    );
};

export default CourseApplicantsPage;
